/**
 * App — root component.
 *
 * Wires together all hooks and renders the layout:
 *   ┌─────────────────────────────────────────────────┐
 *   │  UserList  │  CallScreen (overlay)               │
 *   │            │  ChatPanel        TranscriptPanel   │
 *   │            │  CallHistory                        │
 *   └─────────────────────────────────────────────────┘
 */

import React, { useCallback, useState } from 'react';
import { useWebSocket, WsMessage }        from './hooks/useWebSocket';
import { usePresence }                    from './hooks/usePresence';
import { useWebRTC }                      from './hooks/useWebRTC';
import { useTranscript }                  from './hooks/useTranscript';
import { UserList }                       from './components/UserList';
import { CallScreen }                     from './components/CallScreen';
import { ChatPanel }                      from './components/ChatPanel';
import { TranscriptPanel }                from './components/TranscriptPanel';
import { CallHistory }                    from './components/CallHistory';
import type { ChatMessage }               from './types';

// ── Config — read from Vite env vars ───────────────────────────────
const WS_ENDPOINT  = import.meta.env.VITE_WS_ENDPOINT  as string;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL  as string;

// For the POC, userId is stored in localStorage.
// Replace with a real auth flow (Cognito, Auth0, etc.) in production.
function getOrCreateUserId(): string {
  let id = localStorage.getItem('voip_user_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('voip_user_id', id);
  }
  return id;
}

const CURRENT_USER_ID = getOrCreateUserId();

export default function App() {
  const [activeTab, setActiveTab]     = useState<'chat' | 'history'>('chat');
  const [selectedUserId, setSelected] = useState<string | null>(null);
  const [messages, setMessages]       = useState<ChatMessage[]>([]);
  const [activeCallLogId, setCallLogId] = useState<string | undefined>();

  // ── Presence ────────────────────────────────────────────────────
  const { onlineUsers, handleMessage: presenceHandler } = usePresence(CURRENT_USER_ID);

  // ── WebRTC ──────────────────────────────────────────────────────
  const handleCallActive = useCallback((callLogId: string) => {
    setCallLogId(callLogId);
    transcript.start(); // auto-start transcription
  }, []);

  const handleCallEnded = useCallback((callLogId: string) => {
    transcript.stop();
    // The transcript fullText is sent back to the backend via call-end message
    // which is dispatched by useWebRTC.hangUp — we hook into that below.
  }, []);

  const webrtc = useWebRTC({
    localUserId: CURRENT_USER_ID,
    sendSignal:  (msg) => wsSend(msg),
    onCallActive: handleCallActive,
    onCallEnded:  handleCallEnded,
  });

  // ── Transcript ──────────────────────────────────────────────────
  const transcript = useTranscript();

  // ── WebSocket message router ────────────────────────────────────
  const handleMessage = useCallback(
    (msg: WsMessage) => {
      presenceHandler(msg);
      webrtc.handleMessage(msg);

      if (msg.type === 'chat') {
        setMessages((prev) => [
          ...prev,
          {
            id:           crypto.randomUUID(),
            from_user_id: msg.from as string,
            to_user_id:   CURRENT_USER_ID,
            body:         msg.body as string,
            ts:           (msg.ts as string) ?? new Date().toISOString(),
            callLogId:    msg.callLogId as string | undefined,
          },
        ]);
      }
    },
    [presenceHandler, webrtc]
  );

  const { status: wsStatus, send: wsSend } = useWebSocket({
    url:       WS_ENDPOINT,
    userId:    CURRENT_USER_ID,
    onMessage: handleMessage,
  });

  // ── Actions ──────────────────────────────────────────────────────
  const handleCallUser = (userId: string) => {
    setSelected(userId);
    webrtc.startCall(userId, true);
  };

  const handleChatUser = (userId: string) => {
    setSelected(userId);
    setActiveTab('chat');
  };

  const handleSendChat = (body: string) => {
    if (!selectedUserId) return;
    const msg: ChatMessage = {
      id:           crypto.randomUUID(),
      from_user_id: CURRENT_USER_ID,
      to_user_id:   selectedUserId,
      body,
      ts:           new Date().toISOString(),
      callLogId:    activeCallLogId,
    };
    setMessages((prev) => [...prev, msg]);
    wsSend({ type: 'chat', to: selectedUserId, body, callLogId: activeCallLogId });
  };

  const handleHangUp = () => {
    // Pass transcript into the call-end signal
    (webrtc.hangUp as (silent?: boolean, transcript?: string) => void)(
      false,
      transcript.fullText
    );
    transcript.stop();
    setCallLogId(undefined);
  };

  const visibleMessages = messages.filter(
    (m) =>
      (m.from_user_id === selectedUserId && m.to_user_id === CURRENT_USER_ID) ||
      (m.from_user_id === CURRENT_USER_ID && m.to_user_id === selectedUserId)
  );

  const isCallActive = ['calling', 'ringing', 'active'].includes(webrtc.callState);

  return (
    <div className="app">
      {/* ── Top bar ─────────────────────────────────────────────── */}
      <header className="app__header">
        <span className="app__logo">📞 VoIP POC</span>
        <span className={`app__ws-status app__ws-status--${wsStatus}`}>
          {wsStatus === 'open' ? '🟢 Connected' : wsStatus === 'connecting' ? '🟡 Connecting…' : '🔴 Offline'}
        </span>
        <span className="app__user-id">
          You: <code>{CURRENT_USER_ID.slice(0, 8)}…</code>
        </span>
      </header>

      <div className="app__body">
        {/* ── Sidebar: online users ────────────────────────────── */}
        <aside className="app__sidebar">
          <UserList
            users={onlineUsers}
            currentUserId={CURRENT_USER_ID}
            onCall={handleCallUser}
            onChat={handleChatUser}
          />
        </aside>

        {/* ── Main area ────────────────────────────────────────── */}
        <main className="app__main">
          {/* Call overlay */}
          {isCallActive && (
            <CallScreen
              callState={webrtc.callState}
              localStream={webrtc.localStream}
              remoteStream={webrtc.remoteStream}
              isMuted={webrtc.isMuted}
              isCameraOff={webrtc.isCameraOff}
              remoteUserId={selectedUserId}
              incomingCall={webrtc.incomingCall}
              onAnswer={webrtc.answerCall}
              onReject={webrtc.rejectCall}
              onHangUp={handleHangUp}
              onToggleMute={webrtc.toggleMute}
              onToggleCamera={webrtc.toggleCamera}
            />
          )}

          {/* Tab bar */}
          <nav className="app__tabs">
            <button
              className={`app__tab ${activeTab === 'chat' ? 'app__tab--active' : ''}`}
              onClick={() => setActiveTab('chat')}
            >
              Chat
            </button>
            <button
              className={`app__tab ${activeTab === 'history' ? 'app__tab--active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              Call History
            </button>
          </nav>

          {activeTab === 'chat' && (
            <div className="app__chat-row">
              <ChatPanel
                messages={visibleMessages}
                currentUserId={CURRENT_USER_ID}
                recipientId={selectedUserId}
                callLogId={activeCallLogId}
                onSend={handleSendChat}
              />
              {isCallActive && (
                <TranscriptPanel
                  lines={transcript.lines}
                  isListening={transcript.isListening}
                  supported={transcript.supported}
                  onStart={transcript.start}
                  onStop={transcript.stop}
                  onClear={transcript.clear}
                />
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <CallHistory userId={CURRENT_USER_ID} apiBaseUrl={API_BASE_URL} />
          )}
        </main>
      </div>
    </div>
  );
}
